/**
 * The order the views are listed in.
 *
 * A dialog of its own rather than drag handles in the switcher, and that is the
 * same decision M5 took about workspaces: **a Radix menu that is also a drag
 * target fights itself over every arrow key.** The switcher is a dropdown, so
 * its arrows already move the highlight, and the keyboard sensor needs the same
 * arrows once a row has been picked up. Moving the drag surface somewhere with
 * no roving focus is what makes the keyboard path work at all -- and reordering
 * views is a rare, deliberate thing, unlike switching between them.
 *
 * Every row is a plain list item here, not a link: this is about *where* the
 * views are, not about going to one.
 */
import { useState } from 'react'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { GripVertical } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { View } from '@/api/types'
import { byOrder } from '@/shared/lib/order'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'

export function ReorderViewsDialog({
  views,
  onOpenChange,
  onReorder,
}: {
  views: readonly View[]
  onOpenChange: (open: boolean) => void
  onReorder: (viewIds: number[]) => void
}) {
  const { t } = useTranslation()

  // Held here as well as sent, so the list follows the drag while the request
  // is in flight and while the dialog is open. The cached views are patched
  // too -- this is not a second source of truth, it is the same order arriving
  // by a shorter road than a re-render of the whole table.
  const [ids, setIds] = useState(() => byOrder(views).map((view) => view.id))

  // A view made or deleted in another tab while this was open: reconciled by
  // keeping the order for the ones still here and putting the new ones last,
  // which is where the server puts them too.
  const listed = ids
    .flatMap((id) => views.filter((view) => view.id === id))
    .concat(views.filter((view) => !ids.includes(view.id)))

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('view.reorderTitle')}</DialogTitle>
          <DialogDescription>{t('view.reorderDescription')}</DialogDescription>
        </DialogHeader>

        <ul aria-label={t('view.reorderTitle')} className="flex flex-col gap-1">
          <SortableList
            ids={listed.map((view) => view.id)}
            strategy={verticalListSortingStrategy}
            modifiers={[restrictToVerticalAxis]}
            onReorder={(next) => {
              setIds(next)
              onReorder(next)
            }}
          >
            {listed.map((view) => (
              <ViewRow key={view.id} view={view} />
            ))}
          </SortableList>
        </ul>
      </DialogContent>
    </Dialog>
  )
}

function ViewRow({ view }: { view: View }) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(view.id)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded border px-2 py-1.5 ${
        isDragging ? 'opacity-60' : ''
      }`}
    >
      <button
        type="button"
        aria-label={t('view.reorderMove', { name: view.name })}
        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
        {...handleProps}
      >
        <GripVertical className="size-4" />
      </button>
      <span className="truncate text-sm">{view.name}</span>
    </li>
  )
}
